import { useCallback, useEffect, useRef, useState } from "react";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import {
  addCategory,
  addSubCategory,
  categoryTree,
  disableCategory,
  enableCategory,
  fetchCategory,
  updateCategory,
} from "@/apis/equipment";
import {
  EquipmentCategory,
  EquipmentCategoryParams,
} from "@/types/equipment";
import {
  findEquipmentCategory,
  findEquipmentCategoryPath,
} from "../utils/categoryTree";

// 管理设备类目树、当前选中类目及类目操作
const useEquipmentCategories = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  // 类目树和选中状态
  const [visibleTree, setVisibleTree] = useState<EquipmentCategory[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [selectedCategory, setSelectedCategory] =
    useState<EquipmentCategory>();
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  // 树、详情和操作分别维护加载状态
  const [treeLoading, setTreeLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const selectedIdRef = useRef<string>(); // 保存最新选中类目
  const treeControllerRef = useRef<AbortController | null>(null); // 类目树请求
  const detailControllerRef = useRef<AbortController | null>(null); // 类目详情请求

  // 统一显示类目请求错误
  const showRequestError = useCallback((error: unknown) => {
    notification.error({
      message: t("equipment.categoryFetchFailed"),
      description: errorCodes[Number(error)] ?? String(error),
    });
  }, [notification, t]);

  // Hook 卸载时取消未完成的请求
  useEffect(() => () => {
    treeControllerRef.current?.abort();
    detailControllerRef.current?.abort();
  }, []);

  // 清空当前选中类目及详情
  const clearSelection = useCallback(() => {
    detailControllerRef.current?.abort();
    detailControllerRef.current = null;
    selectedIdRef.current = undefined;
    setSelectedId(undefined);
    setSelectedCategory(undefined);
    setDetailLoading(false);
  }, []);

  // 加载指定类目详情，并取消上一次详情请求
  const loadCategoryDetail = useCallback(async (id: string) => {
    detailControllerRef.current?.abort();
    const controller = new AbortController();
    detailControllerRef.current = controller;
    selectedIdRef.current = id;
    setSelectedId(id);
    setSelectedCategory(undefined);
    setDetailLoading(true);

    try {
      const detail = await fetchCategory(id, controller);
      // 只有最新请求可以更新详情
      if (detailControllerRef.current === controller) {
        setSelectedCategory(detail);
      }
    } catch (error) {
      if (!controller.signal.aborted) throw error;
    } finally {
      if (detailControllerRef.current === controller) {
        setDetailLoading(false);
      }
    }
  }, []);

  // 选择类目并统一处理加载失败
  const selectCategory = useCallback(async (id: string) => {
    try {
      await loadCategoryDetail(id);
      return true;
    } catch (error) {
      showRequestError(error);
      return false;
    }
  }, [loadCategoryDetail, showRequestError]);

  // 更新可见树，并按指定类目、原选中类目、首个类目的顺序恢复选中项
  const syncVisibleTree = useCallback(async (
    nextVisibleTree: EquipmentCategory[],
    preferredId?: string,
  ) => {
    setVisibleTree(nextVisibleTree);

    const nextSelectedId =
      (preferredId && findEquipmentCategory(nextVisibleTree, preferredId)?.id) ||
      (selectedIdRef.current &&
        findEquipmentCategory(nextVisibleTree, selectedIdRef.current)?.id) ||
      nextVisibleTree[0]?.id;

    if (!nextSelectedId) {
      clearSelection();
      return;
    }

    await loadCategoryDetail(nextSelectedId);
  }, [clearSelection, loadCategoryDetail]);

  // 加载类目树，并在需要时展开指定类目的父级路径
  const loadTree = useCallback(async (preferredId?: string) => {
    treeControllerRef.current?.abort();
    const controller = new AbortController();
    treeControllerRef.current = controller;
    setTreeLoading(true);

    try {
      const nextTree = await categoryTree(controller);
      if (treeControllerRef.current !== controller) return;

      if (preferredId) {
        const selectedPath = findEquipmentCategoryPath(nextTree, preferredId);
        if (selectedPath) {
          setExpandedKeys(currentKeys => Array.from(new Set([
            ...currentKeys,
            ...selectedPath.slice(0, -1),
          ])));
        }
      }
      await syncVisibleTree(nextTree, preferredId);
    } catch (error) {
      if (!controller.signal.aborted) throw error;
    } finally {
      if (treeControllerRef.current === controller) {
        setTreeLoading(false);
      }
    }
  }, [syncVisibleTree]);

  // 首次进入时加载类目树
  useEffect(() => {
    loadTree().catch(showRequestError);
  }, [loadTree, showRequestError]);

  // 新建根类目，成功后刷新并选中新类目
  const createRoot = useCallback(async (body: EquipmentCategoryParams) => {
    setActionLoading(true);
    try {
      const category = await addCategory(body);
      await loadTree(category.id);
      return category;
    } catch (error) {
      showRequestError(error);
      return undefined;
    } finally {
      setActionLoading(false);
    }
  }, [loadTree, showRequestError]);

  // 新建子类目，成功后刷新并选中新类目
  const createChild = useCallback(async (
    parentId: string,
    body: EquipmentCategoryParams,
  ) => {
    setActionLoading(true);
    try {
      const category = await addSubCategory(parentId, body);
      await loadTree(category.id);
      return category;
    } catch (error) {
      showRequestError(error);
      return undefined;
    } finally {
      setActionLoading(false);
    }
  }, [loadTree, showRequestError]);

  // 编辑类目，成功后在本地同步当前详情和树节点
  const editCategory = useCallback(async (
    id: string,
    body: EquipmentCategoryParams,
  ) => {
    setActionLoading(true);
    try {
      const category = await updateCategory(id, body);
      // 只取允许编辑的字段，避免接口返回值覆盖树节点已有的层级关系。
      const editableFields = {
        code: category.code,
        name: category.name,
        desc: category.desc,
      };

      setVisibleTree(currentTree => {
        // 递归更新命中的树节点，避免成功后重新请求类目树和详情接口。
        const updateTree = (
          nodes: EquipmentCategory[],
        ): EquipmentCategory[] => nodes.map(node => {
          if (node.id === id) return { ...node, ...editableFields };
          if (!node.subs?.length) return node;
          return { ...node, subs: updateTree(node.subs) };
        });

        return updateTree(currentTree);
      });
      // 同步右侧当前详情，使界面直接展示接口返回的新值。
      setSelectedCategory(currentCategory =>
        currentCategory?.id === id
          ? { ...currentCategory, ...editableFields }
          : currentCategory);
      return category;
    } catch (error) {
      showRequestError(error);
      return undefined;
    } finally {
      setActionLoading(false);
    }
  }, [showRequestError]);

  // 启用或禁用类目，成功后刷新当前类目
  const setCategoryEnabled = useCallback(async (
    id: string,
    enabled: boolean,
  ) => {
    setActionLoading(true);
    try {
      if (enabled) {
        await enableCategory(id);
      } else {
        await disableCategory(id);
      }
      await loadTree(id);
      return true;
    } catch (error) {
      showRequestError(error);
      return false;
    } finally {
      setActionLoading(false);
    }
  }, [loadTree, showRequestError]);

  return {
    visibleTree,
    selectedId,
    selectedCategory,
    expandedKeys,
    treeLoading,
    detailLoading,
    actionLoading,
    selectCategory,
    createRoot,
    createChild,
    editCategory,
    setCategoryEnabled,
    setExpandedKeys,
  };
};

export default useEquipmentCategories;
